import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { fetchUserTasks } from "../features/tasks/taskThunks";
import { updateTaskStatus } from "../features/tasks/taskSlice";
import { FaEye, FaEdit, FaPlus, FaTrash } from "react-icons/fa";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import FileUploadOutlinedIcon from "@mui/icons-material/FileUploadOutlined";
import SyncOutlinedIcon from "@mui/icons-material/SyncOutlined";
import TaskPageHeader from "./common/TaskPageHeader";
import TaskStatusFilterRow from "./common/TaskStatusFilterRow";
import TaskFilterDialog from "./common/TaskFilterDialog";
import CustomButton from "./Custom/CustomButton";
import "./TaskBoard.css";
import TaskDetailsModal from "./TaskDetailsModal";
import AddTaskModal from "./AddTaskModal";
import EditTaskModal from "./EditTaskModal";
import BulkUploadModal from "./BulkUploadModal";
import TaskStatusModal from "./TaskStatusModal";
import axiosInstance from "../api/axiosInstance";
import AppDataTable from "./common/AppDataTable";
import AppDrawer from "./common/AppDrawer";
import { captureEvent, captureError } from "../utils/posthogUtils";
import { POSTHOG_EVENTS } from "../constants/posthog-events";
import UserAvatarTooltip from "./common/UserAvatarTooltip";
import { toast } from "react-toastify";
import editIcon from "../assets/edit.svg";
import binIcon from "../assets/bin.svg";
import viewIcon from "../assets/view.svg";
import useTableUrlState from "../hooks/useTableUrlState";
import useFilterFacets from "../hooks/useFilterFacets";
import { useWorkLogPrompt } from "../hooks/useWorkLogPrompt";
import { Box, IconButton, Tooltip } from "@mui/material";
import InlineStatusDropdown from "./InlineStatusDropdown";
import { buildEmployeeAssigneeOption } from "./common/EmployeeAssigneeOption";
import {
    statusCards,
    priorityFilterOptions,
    ageingFilterOptions,
    badgeClassLookup,
    formatDate,
    resolveTaskStartDate,
    resolveTaskCompletionTimestamp,
    calculateAgeingDisplay,
    formatAgeing,
    resolveTaskTicketId,
    resolveTaskProjectName,
} from "../utils/taskUtils";


/** Staged / URL column filter `id` → `/api/task/me` query parameter name. */
function taskMeQueryParamName(filterId) {
    switch (filterId) {
        case "department":
            return "departmentId";
        case "project":
            return "projectId";
        case "group_id":
            return "groupId";
        case "ageing":
            return "overdue";
        case "due_date_start":
            return "startDate";
        case "due_date_end":
            return "endDate";
        case "createdBy":
            return "createdBy";
        default:
            return filterId;
    }
}

/** Builds summary card config from a metrics object returned by the summary API. */
function buildSummaryCards(m = {}) {
    return [
        {
            key: "total",
            label: "Total Tasks",
            value: m.totalTasks ?? 0,
            subtitle: "Matching active filters",
            tooltip: {
                text: "Count of tasks matching your active filters.",
            },
        },
        {
            key: "completion",
            label: "% Completion",
            value: `${Math.round(m.completionRate ?? 0)}%`,
            subtitle: `${m.completedTasks ?? 0}/${m.totalTasks ?? 0} tasks completed`,
            tone: "warning",
            tooltip: {
                formula: "(Completed tasks ÷ Total tasks) x 100",
                text: "Measures output, not speed.",
                subtext: "(based on active filters)",
            },
        },
        {
            key: "overdue",
            label: "% Overdue",
            value: `${Math.round(m.overduePercentage ?? 0)}%`,
            subtitle: `${m.overdueTasks ?? 0}/${m.totalTasks ?? 0} overdue tasks`,
            tone: "danger",
            tooltip: {
                formula: "(Open tasks past Due Date ÷ Total tasks) x 100",
                text: "Calculated only on tasks not yet Completed. Not the inverse of % Completion.",
                subtext: "(based on active filters)",
            },
        },
        {
            key: "tat",
            label: "% TAT",
            value: `${Math.round(m.tatAdherence ?? 0)}%`,
            subtitle: "On-time adherence rate",
            tone: "success",
            tooltip: {
                formula: "(Tasks completed within Due Date ÷ Completed tasks) x 100",
                text: "Measures execution speed on closed work only.",
                subtext: "(based on active filters)",
            },
        },
        {
            key: "logged",
            label: "Logged Hours",
            value: `${Math.round(((m.loggedHours ?? 0) / 3600) * 100) / 100}h`,
            subtitle: `${Math.round(((m.loggedHours ?? 0) / 3600) * 100) / 100}/${Math.round(((m.estimatedHours ?? 0) / 3600) * 100) / 100} hours logged`,
            tooltip: {
                text: "Total hours logged against tasks in filter.",
                subtext: "(based on active filters)",
            },
            hidden: true,
        },
        {
            key: "openAge",
            label: "Open Age (in days)",
            value: Math.round(m.avgOpenAgeDays ?? 0),
            subtitle: "Avg days tasks are open",
            tone: "warning",
            tooltip: {
                text: "Average days tasks have stayed open (excludes Completed).",
                subtext: "(based on active filters)",
            },
        },
    ];
}

/** Filter categories always shown in the filter dialog. */
const BASE_FILTER_CATEGORIES = [
    { key: "department", label: "Department", stagedId: "department" },
    { key: "priority", label: "Priority", stagedId: "priority" },
    { key: "status", label: "Status", stagedId: "status" },
    { key: "assignee", label: "Assignee", stagedId: "assignedTo" },
    { key: "assigner", label: "Created By", stagedId: "assignedBy" },
    { key: "overdue", label: "Overdue", stagedId: "ageing" },
    { key: "project", label: "Project", stagedId: "project" },
];

/** Team Members category — only added when viewing My Team's Tasks. */
const TEAM_MEMBERS_CATEGORY = { key: "teamMembers", label: "Team Members", stagedId: "teamMembers" };

const splitFilterValues = (v) => (v ? String(v).split(",").filter(Boolean) : []);

const UUID_QUERY_PARAMS = new Set([
    "departmentId",
    "projectId",
    "groupId",
    "assignedBy",
    "assignedTo",
    "teamMembers",
    "employeeId",
    "createdBy",
]);

// ─────────────────────────────────────────────────────────────────────────────

const TaskBoard = () => {
    // Determine the active tab early so we can use completely isolated persistence keys
    const [searchParams, setSearchParams] = useSearchParams();
    const urlTab = searchParams.get("tab") === "team" ? "team" : "my";

    // ── Resolve Redux user early — needed for user-scoped persistence key ──────
    const reduxUser = useSelector((state) => state.auth.user);

    const {
        pagination,
        sorting,
        globalFilter,
        columnFilters,
        onPaginationChange,
        onSortingChange,
        onGlobalFilterChange,
        onColumnFiltersChange,
        applyFiltersAndSearch,
    } = useTableUrlState({
        pageSize: 10,
        persistenceKey: urlTab === "team" ? 'taskboard_team' : 'taskboard_my',
        userId: reduxUser?.userId || reduxUser?.id || reduxUser?.sub,
    });

    // ── Redux task state ──────────────────────────────────────────────────────
    const dispatch = useDispatch();
    const { tasks: rawTasks, total: totalRows, loading: isLoading } = useSelector(
        (state) => state.tasks
    );
    const tasks = Array.isArray(rawTasks) ? rawTasks : [];

    const [isRefetching, setIsRefetching] = useState(false);
    const [userId, setUserId] = useState(null);
    const [currentUserInfo, setCurrentUserInfo] = useState(null);
    const [rowSelection, setRowSelection] = useState({});
    const [bulkCompleteLoading, setBulkCompleteLoading] = useState(false);
    const [showBulkCompleteModal, setShowBulkCompleteModal] = useState(false);
    const [bulkCompleteTasks, setBulkCompleteTasks] = useState([]);


    // ── UI visibility ─────────────────────────────────────────────────────────
    const [showTaskDrawer, setShowTaskDrawer] = useState(false);
    const [selectedTask, setSelectedTask] = useState(null);
    const { workLogOpenSignal, promptWorkLogIfCompleted, resetWorkLogSignal } = useWorkLogPrompt();
    const [refreshSignal, setRefreshSignal] = useState(0);
    const [showAddTask, setShowAddTask] = useState(false);
    const [showBulkUpload, setShowBulkUpload] = useState(false);
    const [filterAnchorEl, setFilterAnchorEl] = useState(null);
    // Live draft from the filter dialog — tracks checkbox state before Apply is clicked
    const [dialogDraft, setDialogDraft] = useState({});

    // Active header tab, synced to the `tab` URL param. Both tabs currently fetch
    // the same "My Tasks" data; a future API parameter will differentiate "My Team's Tasks".
    const hasSubordinates = currentUserInfo?.subordinatesCount?.total > 0;
    const activeTab = urlTab === "team" && hasSubordinates ? "team" : "my";
    const activeFilterCategories = activeTab === "team"
        ? [...BASE_FILTER_CATEGORIES, TEAM_MEMBERS_CATEGORY]
        : BASE_FILTER_CATEGORIES;

    // ── Staged Filters ────────────────────────────────────────────────────────
    const [stagedFilters, setStagedFilters] = useState({});
    const [stagedSearch, setStagedSearch] = useState("");

    // Sync from URL state when it changes (e.g., on mount or tab switch)
    useEffect(() => {
        const mapped = {};
        columnFilters.forEach(f => {
            let val = f.value;
            if (typeof val === "object" && val !== null) {
                val = val.id || val.userId || val.value || "";
            }
            const columnToStaged = {
                department: "department",
                priority: "priority",
                status: "status",
                assignedTo: "assignedTo",
                createdBy: "assignedBy",
                ageing: "ageing",
                project: "project",
                teamMembers: "teamMembers",
            };
            const stagedId = columnToStaged[f.id] || f.id;
            mapped[stagedId] = String(val);
        });
        setStagedFilters(mapped);
        setStagedSearch(globalFilter || "");
    }, [columnFilters, globalFilter]);

    const handleTabChange = useCallback((value) => {
        const uid = reduxUser?.userId || reduxUser?.id || reduxUser?.sub;
        const baseKey = value === "team" ? "taskboard_team" : "taskboard_my";
        const nextStorageKey = uid ? `table_state_${baseKey}_u${uid}` : `table_state_${baseKey}`;
        const saved = localStorage.getItem(nextStorageKey);

        // Start from a clean slate OR the saved state for the target tab
        const next = saved ? new URLSearchParams(saved) : new URLSearchParams();

        if (value === "team") {
            next.set("tab", "team");
        } else {
            next.delete("tab");
            // Clear teamMembers from live draft when leaving team tab
            setStagedFilters((prev) => {
                if (!prev.teamMembers) return prev;
                const draft = { ...prev };
                delete draft.teamMembers;
                return draft;
            });
        }

        setSearchParams(next, { replace: true });
    }, [setSearchParams, setStagedFilters]);

    const [summaryData, setSummaryData] = useState(null);
    const [isSummaryLoading, setIsSummaryLoading] = useState(false);
    const statusCounts = summaryData?.statusCounts || null;

    const summaryCards = useMemo(() => {
        return buildSummaryCards(summaryData?.metrics || {});
    }, [summaryData]);


    // ── Status & Edit modal ───────────────────────────────────────────────────
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [showEditTaskModal, setShowEditTaskModal] = useState(false);
    const [actionTask, setActionTask] = useState(null);

    // ── Delegate options ──────────────────────────────────────────────────────
    const [delegateOptions, setDelegateOptions] = useState([]);
    const [isExporting, setIsExporting] = useState(false);



    // ── Facets ────────────────────────────────────────────────────────────────
    // When the filter dialog is closed, facets are scoped to applied (stagedFilters).
    // When the dialog is open, facets cascade from dialogDraft — updated on left-tab
    // switch (and seeded on open), not on every checkbox toggle. Apply uses stagedFilters.
    const facetActiveParams = useMemo(() => {
        const p = {};
        // Use applied search only (Enter), not the in-progress typed value.
        if (globalFilter) p.search = globalFilter;
        if (stagedFilters.status && stagedFilters.status !== "All") p.status = stagedFilters.status;
        if (stagedFilters.priority) p.priority = stagedFilters.priority;
        if (stagedFilters.department) p.departmentId = stagedFilters.department;
        if (stagedFilters.project) p.projectId = stagedFilters.project;
        if (stagedFilters.group_id) p.groupId = stagedFilters.group_id;
        if (stagedFilters.assignedBy) p.assignedBy = stagedFilters.assignedBy;
        if (stagedFilters.assignedTo) p.assignedTo = stagedFilters.assignedTo;
        if (stagedFilters.teamMembers) p.teamMembers = stagedFilters.teamMembers;
        if (stagedFilters.ageing) p.overdue = stagedFilters.ageing;
        if (stagedFilters.due_date_start) p.startDate = stagedFilters.due_date_start;
        if (stagedFilters.due_date_end) p.endDate = stagedFilters.due_date_end;
        return p;
    }, [stagedFilters, globalFilter]);

    // When dialog is open, build params from the live draft instead so facets cascade
    const facetDraftParams = useMemo(() => {
        const draft = dialogDraft;
        if (!Boolean(filterAnchorEl)) return facetActiveParams;
        const p = { ...facetActiveParams };
        if (draft.department?.length) p.departmentId = draft.department.join(",");
        else delete p.departmentId;
        if (draft.project?.length) p.projectId = draft.project.join(",");
        else delete p.projectId;
        if (draft.priority?.length) p.priority = draft.priority.join(",");
        else delete p.priority;
        if (draft.status?.length) p.status = draft.status.join(",");
        else delete p.status;
        if (draft.assignee?.length) p.assignedTo = draft.assignee.join(",");
        else delete p.assignedTo;
        if (draft.teamMembers?.length) p.teamMembers = draft.teamMembers.join(",");
        else delete p.teamMembers;
        if (draft.assigner?.length) p.assignedBy = draft.assigner.join(",");
        else delete p.assignedBy;
        if (draft.overdue?.length) p.overdue = draft.overdue.join(",");
        else delete p.overdue;
        return p;
    }, [dialogDraft, facetActiveParams, filterAnchorEl]);

    const facetType = activeTab === "team" ? "my-team" : "me";
    const { facetOptions } = useFilterFacets(facetType, facetDraftParams);


    // Migration mapping for legacy URL filters to Task API standard
    const legacyMap = {
        departmentId: "department",
        department_id: "department",
        projectId: "project",
        project_id: "project",
        groupId: "group_id",
        employee: "assignedBy",
        employee_id: "assignedBy",
        overdue: "ageing",
        startDate: "due_date_start",
        endDate: "due_date_end",
    };

    useEffect(() => {
        if (columnFilters.length > 0 || globalFilter) {
            const initial = {};
            columnFilters.forEach((f) => {
                const newKey = legacyMap[f.id] || f.id;
                initial[newKey] = f.value;
            });
            setStagedFilters(initial);
            setStagedSearch(globalFilter);
        }
    }, [columnFilters, globalFilter]); // Sync staged UI when URL state changes (e.g. restoration)

    // Derived header-filter values for UI populating
    const currentDept = stagedFilters.department || "";
    const currentProject = stagedFilters.project || "";
    const currentGroup = stagedFilters.group_id || "";
    const currentEmp = stagedFilters.assignedBy || "";
    const currentAgeing = stagedFilters.ageing || "";
    const currentPrio = stagedFilters.priority || "";
    const currentStartDate = stagedFilters.due_date_start || "";
    const currentEndDate = stagedFilters.due_date_end || "";
    const currentStatus = stagedFilters.status || "All";

    // ── Redux selectors ───────────────────────────────────────────────────────
    const departments = useSelector((state) => state.departments.departments) || [];
    const projects = useSelector((state) => state.projects.allProjects) || [];
    const groups = useSelector((state) => state.groups.groups) || [];
    const reduxEmployees = useSelector((state) => state.employees.employees) || [];

    // ── New filter dialog: options, current selection, apply handler ────────────
    const filterOptionsByCategory = useMemo(() => {
        const deptList = facetOptions.departments.length > 0 ? facetOptions.departments : departments;
        const projectList = facetOptions.projects.length > 0 ? facetOptions.projects : projects;
        // Prefer facet assignees; fall back to redux employee list
        const assigneeList = facetOptions.assignees.length > 0 ? facetOptions.assignees : reduxEmployees;

        const mapPerson = (e) => {
            const id = e.id || e.userId;
            const fn = e.first_name || e.FirstName || e.firstName || "";
            const ln = e.last_name || e.LastName || e.lastName || "";
            const label = `${fn} ${ln}`.trim() || e.email || e.Email || String(id || "");
            return { value: String(id || ""), label };
        };

        // Prefer API-provided statuses/priorities when available
        const statusOpts = facetOptions.statuses.length > 0
            ? facetOptions.statuses.map((s) => {
                const match = statusCards.find((c) => c.label.toLowerCase() === s.toLowerCase());
                const val = match ? match.label : s;
                return { value: val, label: val };
            })
            : statusCards.filter((c) => c.label !== "All").map((c) => ({ value: c.label, label: c.label }));

        const priorityOrder = { urgent: 1, high: 2, medium: 3, low: 4 };
        const priorityOpts = (facetOptions.priorities.length > 0
            ? facetOptions.priorities.map((p) => {
                const match = priorityFilterOptions.find(
                    (o) => o.value.toLowerCase() === String(p).toLowerCase()
                );
                return {
                    value: String(p),
                    label: match?.label || (String(p).charAt(0).toUpperCase() + String(p).slice(1)),
                };
            })
            : priorityFilterOptions.map((o) => ({ value: o.value, label: o.label })))
            .sort((a, b) => (priorityOrder[String(a.value).toLowerCase()] || 99) - (priorityOrder[String(b.value).toLowerCase()] || 99));

        const assignerList = facetOptions.assigners.length > 0 ? facetOptions.assigners : [];

        const selfId = userId ? String(userId) : "";
        const mappedAssignees = assigneeList.map(mapPerson).filter((o) => o.value);
        // "Me" only on My Tasks (not My Team's Tasks): pin at top and hide own name from the list.
        const assigneeOpts =
            activeTab === "my" && selfId
                ? [
                    { value: selfId, label: "Me" },
                    ...mappedAssignees.filter((o) => o.value !== selfId),
                ]
                : mappedAssignees;

        return {
            department: deptList.map((d) => ({ value: String(d.id), label: d.name || "" })),
            project: projectList.map((p) => ({ value: String(p.id), label: p.name || "" })),
            priority: priorityOpts,
            status: statusOpts,
            assignee: assigneeOpts,
            assigner: assignerList.map(mapPerson).filter((o) => o.value),
            overdue: ageingFilterOptions.map((o) => ({ value: o.value, label: o.label })),
        };
    }, [facetOptions, departments, projects, reduxEmployees, userId, activeTab]);

    const filterSelected = useMemo(() => {
        const status = stagedFilters.status && stagedFilters.status !== "All"
            ? splitFilterValues(stagedFilters.status)
            : [];
        return {
            department: splitFilterValues(stagedFilters.department),
            priority: splitFilterValues(stagedFilters.priority),
            status,
            assignee: splitFilterValues(stagedFilters.assignedTo),
            assigner: splitFilterValues(stagedFilters.assignedBy),
            overdue: splitFilterValues(stagedFilters.ageing),
            project: splitFilterValues(stagedFilters.project),
            teamMembers: splitFilterValues(stagedFilters.teamMembers),
        };
    }, [stagedFilters]);

    const activeFilterCount = useMemo(
        () => activeFilterCategories.reduce((n, c) => n + (filterSelected[c.key]?.length ? 1 : 0), 0),
        [filterSelected, activeFilterCategories]
    );

    const handleApplyDialogFilters = useCallback((selected) => {
        const next = { ...stagedFilters };
        activeFilterCategories.forEach(({ key, stagedId }) => {
            const vals = selected[key] || [];
            if (vals.length) {
                next[stagedId] = vals.join(",");
            } else {
                delete next[stagedId];
            }
        });
        setStagedFilters(next);

        const newFilters = Object.entries(next)
            .filter(([, value]) => value !== "" && value !== null && value !== undefined)
            .map(([id, value]) => ({ id, value }));
        applyFiltersAndSearch(newFilters, stagedSearch);
        setFilterAnchorEl(null);
        setDialogDraft({});
    }, [stagedFilters, applyFiltersAndSearch, stagedSearch, activeFilterCategories]);

    // ── Resolve current user ──────────────────────────────────────────────────
    useEffect(() => {
        if (reduxUser) {
            setUserId(reduxUser.userId || reduxUser.id || reduxUser.sub);
            setCurrentUserInfo(reduxUser);
        }
    }, [reduxUser]);

    // ── Build delegate options (same employee mapping as Add Task "Assign To") ─
    useEffect(() => {
        const items = Array.isArray(reduxEmployees) ? reduxEmployees : [];
        const options = items
            .map((item) => {
                const opt = buildEmployeeAssigneeOption(item);
                if (!opt.value || opt.value === "undefined") return null;
                return {
                    ...opt,
                    meta: { email: opt.email },
                    searchText: `${opt.name || ""} ${opt.email || ""} ${opt.department || ""}`
                        .trim()
                        .toLowerCase(),
                };
            })
            .filter(Boolean);

        // Append current user if missing (same as before).
        if (userId && currentUserInfo) {
            const selfId = String(userId);
            if (!options.some((o) => o.value === selfId)) {
                const selfOpt = buildEmployeeAssigneeOption({
                    ...currentUserInfo,
                    userId: selfId,
                    id: selfId,
                });
                options.push({
                    ...selfOpt,
                    meta: { email: selfOpt.email },
                    searchText: `${selfOpt.name || ""} ${selfOpt.email || ""}`
                        .trim()
                        .toLowerCase(),
                });
            }
        }

        setDelegateOptions(options);
    }, [reduxEmployees, userId, currentUserInfo]);

    // ── Export tasks ──────────────────────────────────────────────────────────
    const handleExportTasks = async () => {
        setIsExporting(true);
        try {
            const params = { type: activeTab === "team" ? "my-team" : "me" };
            if (globalFilter) params.search = globalFilter;
            if (sorting?.length) {
                params.sortBy = sorting.map(s => s.id === "due_date" ? "due_date" : s.id).join(',');
                params.sortOrder = sorting.map(s => s.desc ? "desc" : "asc").join(',');
            }
            if (columnFilters && columnFilters.length > 0) {
                columnFilters.forEach((f) => {
                    let val = f.value;
                    if (typeof val === "object" && val !== null) {
                        val = val.id || val.userId || val.value || "";
                    }
                    if ((f.id === "priority" || f.id === "ageing") && typeof val === "string") {
                        val = val.toLowerCase();
                    }
                    if (val !== "All" && val !== "") {
                        const paramName = taskMeQueryParamName(f.id);
                        const finalVal = UUID_QUERY_PARAMS.has(paramName) ? String(val) : val;
                        if (params[paramName]) {
                            params[paramName] = `${params[paramName]},${finalVal}`;
                        } else {
                            params[paramName] = finalVal;
                        }
                    }
                });
            }

            const response = await axiosInstance.get("/api/task/list/export", {
                params,
                responseType: "blob",
            });
            const disposition = response.headers?.["content-disposition"] || "";
            const match = disposition.match(/filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/);
            const filename = match ? match[1].replace(/['"]/g, "") : "my_tasks_export.xlsx";
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
            toast.success("Tasks exported successfully!");
            captureEvent(POSTHOG_EVENTS.TASKS_EXPORTED, { source: "my_tasks" });
        } catch (err) {
            toast.error(err.message || "Failed to export tasks. Please try again.");
            captureError(POSTHOG_EVENTS.TASKS_EXPORT_FAILED, err, { source: "my_tasks" });
        } finally {
            setIsExporting(false);
        }
    };

    // ── Status filter helper ──────────────────────────────────────────────────
    const setStatusFilter = (val) => {
        const nextStatus = val === "All" ? "" : val;
        // Keep the staged UI state in sync.
        setStagedFilters((prev) => ({
            ...prev,
            status: nextStatus,
        }));
        // Apply status immediately so user doesn't need to click "Apply".
        onColumnFiltersChange((prev) => [
            ...prev.filter((f) => f.id !== "status"),
            ...(nextStatus ? [{ id: "status", value: nextStatus }] : []),
        ]);
    };

    const selectedCreatedByUserId = useMemo(() => {
        // Pass the full comma-separated value so the avatar row highlights ALL selected users.
        const val = stagedFilters.assignedBy;
        if (!val) return null;
        return String(val);
    }, [stagedFilters]);

    const handleCreatedBySelect = useCallback((creatorUserId) => {
        const next = { ...stagedFilters };
        if (!creatorUserId) {
            delete next.assignedBy;
        } else {
            next.assignedBy = String(creatorUserId);
        }
        setStagedFilters(next);
        const newFilters = Object.entries(next)
            .filter(([, value]) => value !== "" && value !== null && value !== undefined)
            .map(([id, value]) => ({ id, value }));
        applyFiltersAndSearch(newFilters, stagedSearch);
    }, [stagedFilters, stagedSearch, applyFiltersAndSearch]);

    // ── Fetch tasks ───────────────────────────────────────────────────────────
    useEffect(() => {
        if (!userId) return; // Guard against initial fetch before user is resolved
        let cancelled = false;
        const fetchTasks = async () => {
            if (cancelled) return;
            try {
                // Build params for /api/task/list
                const params = {
                    page: pagination.pageIndex + 1,
                    limit: pagination.pageSize,
                    type: activeTab === "team" ? "my-team" : "me",
                    sortBy: "created_at",
                    sortOrder: "DESC",
                };

                // Search
                if (globalFilter) params.search = globalFilter;

                // Sorting — user-specified overrides the default
                if (sorting?.length) {
                    params.sortBy = sorting.map(s => {
                        if (s.id === "due_date") return "due_date";
                        if (s.id === "createdBy") return "assigned_by";
                        if (s.id === "assignedTo") return "assigned_to";
                        return s.id;
                    }).join(",");
                    params.sortOrder = sorting.map(s => s.desc ? "DESC" : "ASC").join(",");
                }

                // Column filters → flat query params (OpenAPI: status, priority, projectId, …)
                if (columnFilters && columnFilters.length > 0) {
                    columnFilters.forEach((f) => {
                        let val = f.value;
                        if (typeof val === "object" && val !== null) {
                            val = val.id || val.userId || val.value || "";
                        }

                        if ((f.id === "priority" || f.id === "ageing") && typeof val === "string") {
                            val = val.toLowerCase();
                        }

                        if (val !== "All" && val !== "") {
                            let paramName = f.id;
                            if (f.id === "department") paramName = "departmentId";
                            else if (f.id === "project") paramName = "projectId";
                            else if (f.id === "group_id") paramName = "groupId";
                            else if (f.id === "ageing") paramName = "overdue";
                            else if (f.id === "due_date_start") paramName = "startDate";
                            else if (f.id === "due_date_end") paramName = "endDate";
                            else if (f.id === "createdBy") paramName = "assignedBy";

                            const finalVal = UUID_QUERY_PARAMS.has(paramName) ? String(val) : val;
                            if (params[paramName]) {
                                params[paramName] = `${params[paramName]},${finalVal}`;
                            } else {
                                params[paramName] = finalVal;
                            }
                        }
                    });
                }

                dispatch(fetchUserTasks(params)).finally(() => {
                    if (!cancelled) setIsRefetching(false);
                });
            } catch (error) {
                if (!cancelled) console.error("Task fetch error:", error);
            } finally {
                if (!cancelled) { /* Redux handles loading state */ }
            }
        };

        fetchTasks();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [userId, activeTab, pagination.pageIndex, pagination.pageSize, sorting, globalFilter, columnFilters, refreshSignal]);

    // ── Fetch summary data (status counts & metrics) ──────────────────────────
    const fetchSummaryData = useCallback(async (filterParams = {}) => {
        setIsSummaryLoading(true);
        try {
            const { data } = await axiosInstance.get("/api/task/summary", { params: filterParams });
            if (data?.success && data?.data) {
                setSummaryData(data.data);
            } else {
                setSummaryData(null);
            }
        } catch (error) {
            setSummaryData(null);
            console.error("Failed to fetch task summary:", error);
        } finally {
            setIsSummaryLoading(false);
        }
    }, []);

    useEffect(() => {
        if (!userId) return;

        // Build query params for summary API based on applied filters
        const params = {
            type: activeTab === "team" ? "my-team" : "me"
        };
        if (globalFilter) params.search = globalFilter;

        // Map applied column filters to API params
        columnFilters.forEach((f) => {
            let val = f.value;
            if (typeof val === "object" && val !== null) {
                val = val.id || val.userId || val.value || "";
            }
            if ((f.id === "priority" || f.id === "ageing") && typeof val === "string") {
                val = val.toLowerCase();
            }

            if (val !== "All" && val !== "") {
                const paramName = taskMeQueryParamName(f.id);
                const mappedName = paramName === "createdBy" ? "assignedBy" : paramName;
                const finalVal = UUID_QUERY_PARAMS.has(paramName) ? String(val) : val;
                if (params[mappedName]) {
                    params[mappedName] = `${params[mappedName]},${finalVal}`;
                } else {
                    params[mappedName] = finalVal;
                }
            }
        });

        fetchSummaryData(params);
    }, [userId, activeTab, refreshSignal, columnFilters, globalFilter, fetchSummaryData]);

    // ── Action handlers ───────────────────────────────────────────────────────
    const handleActionClick = (task) => {
        setActionTask(task);

        const selfId = String(userId);
        const assignedById = String(task.assignedBy?.userId || task.assignedBy?.id || task.GivenByUserId || "");

        // Fallback checks for other possible creator ID fields in the task object
        const createdById = String(task.created_by?.id || task.created_by || "");
        const assignedBy = String(task.assigned_by || "");

        if (assignedById === selfId || (createdById !== "" && createdById === selfId) || (assignedBy !== "" && assignedBy === selfId)) {
            setShowEditTaskModal(true);
        } else {
            setShowStatusModal(true);
        }
    };

    const handleViewTask = (task, extraParams = {}) => {
        setSelectedTask({ ...task, ...extraParams });
        setShowTaskDrawer(true);
    };

    const handleDeleteTask = async (task) => {
        const title = task.title || "this task";
        if (!window.confirm(`Are you sure you want to delete "${title}"? This cannot be undone.`)) return;
        try {
            // Updated to use bulk API for single deletion as requested
            await axiosInstance.delete(`/api/task/bulk`, {
                data: {
                    taskIds: [task.id],
                    currentUserId: userId
                },
            });
            toast.success("Task deleted successfully");
            captureEvent(POSTHOG_EVENTS.TASK_DELETED, {
                task_id: task.id,
                source: "my_tasks",
                deleted_by: userId || null,
            });
            setRefreshSignal(prev => prev + 1);
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Failed to delete task");
            captureError(POSTHOG_EVENTS.TASK_DELETE_FAILED, error, { task_id: task.id, source: "my_tasks" });
        }
    };

    const openTaskDrawer = useCallback((task) => {
        setSelectedTask(task);
        setShowTaskDrawer(true);
    }, []);

    const handleCompletedStatus = useCallback(
        (task, newStatus) => {
            promptWorkLogIfCompleted(task, newStatus, openTaskDrawer);
        },
        [promptWorkLogIfCompleted, openTaskDrawer]
    );

    // ── Bulk Complete ─────────────────────────────────────────────────────────
    const handleBulkComplete = () => {
        const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
        if (selectedIds.length === 0) return;
        setBulkCompleteTasks(tasks.filter((t) => selectedIds.includes(String(t.id))));
        setShowBulkCompleteModal(true);
    };

    const executeBulkComplete = async () => {
        setBulkCompleteLoading(true);
        setShowBulkCompleteModal(false);
        const ids = bulkCompleteTasks.map((t) => t.id);
        const completedTasks = bulkCompleteTasks;
        try {
            // Bulk update API: POST /api/task/bulk with taskIds and status
            await axiosInstance.post("/api/task/bulk", {
                taskIds: ids,
                status: "Completed",
                currentUserId: userId
            });
            const msg = `${ids.length} task${ids.length !== 1 ? "s" : ""} completed`;
            toast.success(msg);
            captureEvent(POSTHOG_EVENTS.BULK_TASK_STATUS_UPDATED, { count: ids.length, status: "Completed", source: "my_tasks" });
            setRefreshSignal(prev => prev + 1);
            setRowSelection({});
            if (completedTasks.length > 0) {
                handleCompletedStatus(completedTasks[0], "completed");
            }
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Bulk complete failed. Please try again.");
            captureError(POSTHOG_EVENTS.BULK_TASK_STATUS_UPDATE_FAILED, error, { count: ids.length, status: "Completed", source: "my_tasks" });
        } finally {
            setBulkCompleteLoading(false);
        }
    };

    const handleBulkDelete = async () => {
        const selectedIds = Object.keys(rowSelection).filter((k) => rowSelection[k]);
        if (selectedIds.length === 0) return;
        if (!window.confirm(`Are you sure you want to delete ${selectedIds.length} task(s)? This cannot be undone.`)) return;

        setBulkCompleteLoading(true);
        try {
            // Bulk delete API: DELETE /api/task/bulk with taskIds in payload
            await axiosInstance.delete("/api/task/bulk", {
                data: {
                    taskIds: selectedIds,
                    currentUserId: userId
                },
            });
            toast.success(`${selectedIds.length} tasks deleted successfully`);
            setRefreshSignal(prev => prev + 1);
            setRowSelection({});
        } catch (error) {
            toast.error(error?.response?.data?.message || error.message || "Bulk delete failed. Please try again.");
        } finally {
            setBulkCompleteLoading(false);
        }
    };



    // ── Table columns ─────────────────────────────────────────────────────────
    const columns = useMemo(
        () => [
            {
                accessorKey: "ticket_id",
                header: "ID",
                size: 100,
                minSize: 80,
                maxSize: 120,
                enableSorting: false,
                Cell: ({ row }) => {
                    const t = row.original;
                    let ticketData = [];
                    const sourceItems = Array.isArray(t.tasks) ? t.tasks : (Array.isArray(t.assignees) ? t.assignees : []);

                    if (sourceItems.length > 0) {
                        ticketData = sourceItems.map(item => ({
                            ticketId: resolveTaskTicketId(item),
                            actualId: item.id || item.taskId || item.TaskId
                        })).filter(td => td.ticketId);
                    } else {
                        const ticket = resolveTaskTicketId(t);
                        if (ticket) ticketData = [{ ticketId: ticket, actualId: t.id }];
                    }

                    // Deduplicate
                    const uniqueTicketData = [];
                    const seen = new Set();
                    ticketData.forEach(td => {
                        if (!seen.has(td.ticketId)) {
                            uniqueTicketData.push(td);
                            seen.add(td.ticketId);
                        }
                    });

                    const MAX_VISIBLE = 2;
                    const visibleTickets = uniqueTicketData.slice(0, MAX_VISIBLE);
                    const overflowTickets = uniqueTicketData.slice(MAX_VISIBLE);

                    const handleIdClick = (e, id) => {
                        e.stopPropagation();
                        const url = `/task/${id}`;
                        window.open(url, "_blank");
                    };

                    return (
                        <div style={{ display: "flex", flexWrap: "wrap", gap: "2px 4px", alignItems: "center" }}>
                            {visibleTickets.map((td, idx) => (
                                <React.Fragment key={td.ticketId || idx}>
                                    <span
                                        onClick={(e) => handleIdClick(e, td.actualId)}
                                        style={{ color: "#2563EB", fontWeight: 500, fontSize: "11px", cursor: "pointer" }}
                                    >
                                        {td.ticketId}
                                    </span>
                                    {idx < visibleTickets.length - 1 && <span style={{ color: '#cbd5e1', fontSize: '10px' }}>,</span>}
                                </React.Fragment>
                            ))}
                            {overflowTickets.length > 0 && (
                                <Tooltip
                                    title={
                                        <Box sx={{ py: 0.5, px: 0.5 }}>
                                            <Box sx={{ fontWeight: 600, fontSize: 11, mb: 0.5, opacity: 0.75, letterSpacing: "0.03em" }}>
                                                All Ticket IDs
                                            </Box>
                                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                                                {uniqueTicketData.map((td) => (
                                                    <Box
                                                        key={td.ticketId}
                                                        onClick={(e) => handleIdClick(e, td.actualId)}
                                                        sx={{
                                                            fontSize: 11,
                                                            lineHeight: 1.8,
                                                            cursor: "pointer",
                                                            "&:hover": { textDecoration: "underline", color: "#60a5fa" }
                                                        }}
                                                    >
                                                        {td.ticketId}
                                                    </Box>
                                                ))}
                                            </Box>
                                        </Box>
                                    }
                                    interactive
                                    arrow
                                    placement="top"
                                    enterDelay={150}
                                >
                                    <Box
                                        component="span"
                                        sx={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            fontSize: "10px",
                                            fontWeight: 700,
                                            color: "#2563EB",
                                            bgcolor: "#eff6ff",
                                            border: "1px solid #bfdbfe",
                                            borderRadius: "10px",
                                            px: "6px",
                                            py: "1px",
                                            cursor: "default",
                                            lineHeight: 1.6,
                                            userSelect: "none",
                                            whiteSpace: "nowrap",
                                        }}
                                    >
                                        +{overflowTickets.length}
                                    </Box>
                                </Tooltip>
                            )}
                            {uniqueTicketData.length === 0 && <span style={{ color: "#94a3b8" }}>—</span>}
                        </div>
                    );
                },
            },
            {
                accessorKey: "title",
                header: "Task Name",
                size: 180,
                minSize: 150,
                maxSize: 220,
                muiTableBodyCellProps: {
                    sx: {
                        fontSize: '11px',
                        color: '#212529',
                        maxWidth: 180,
                        width: 180,
                        verticalAlign: "middle",
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                    },
                },
                muiTableHeadCellProps: {
                    sx: {
                        backgroundColor: '#f8f9fa',
                        fontWeight: 700,
                        fontSize: '11px',
                        letterSpacing: '0.5px',
                        borderBottom: '2px solid #dee2e6',
                        maxWidth: 180,
                        width: 180
                    },
                },
                Cell: ({ row }) => {
                    const t = row.original;
                    return (
                        <div className="taskboard-task-name-cell">
                            <div className="taskboard-task-name-title">
                                {t.title || "—"}
                            </div>
                        </div>
                    );
                },
            },
            {
                id: "project",
                accessorFn: (row) => resolveTaskProjectName(row),
                header: "Project",
                size: 150,
                minSize: 120,
                enableSorting: false,
                muiTableBodyCellProps: {
                    sx: {
                        whiteSpace: 'normal',
                        wordWrap: 'break-word',
                    }
                },
                Cell: ({ row }) => {
                    const name = resolveTaskProjectName(row.original);
                    if (!name) return <span className="taskboard-meta-muted">—</span>;
                    return <span className="taskboard-project-name">{name}</span>;
                },
            },
            {
                accessorKey: "priority",
                header: "Priority",
                size: 110,
                enableSorting: false,
                Cell: ({ row }) => {
                    const raw = (row.original.priority || "").toString().trim();
                    if (!raw) return <span className="taskboard-meta-muted">—</span>;
                    const key = raw.toLowerCase();
                    const label =
                        priorityFilterOptions.find((o) => o.value === key)?.label ||
                        raw.charAt(0).toUpperCase() + raw.slice(1).toLowerCase();
                    const pillSuffix = { urgent: "urgent", high: "high", medium: "medium", low: "low" }[key] || "default";
                    return (
                        <span className={`taskboard-priority-pill taskboard-priority-${pillSuffix}`}>
                            {label}
                        </span>
                    );
                },
            },
            {
                accessorKey: "due_date",
                header: "End Date",
                size: 100,
                Cell: ({ row }) => <span style={{ fontSize: '11px', color: '#212529' }}>{formatDate(row.original.due_date || row.original.dueDate)}</span>,
            },
            {
                accessorKey: "ageing",
                header: "Overdue",
                size: 100,
                enableSorting: false,
                Cell: ({ row }) => <span style={{ fontSize: '11px', color: '#212529' }}>{formatAgeing(row.original.ageing)}</span>,
            },
            {
                accessorKey: "status",
                header: "Status",
                size: 110,
                enableSorting: false,
                Cell: ({ row }) => (
                    <InlineStatusDropdown
                        task={row.original}
                        userId={userId}
                        size="small"
                        onDelegateClick={(t) => {
                            setActionTask({ ...t, status: "delegated" });
                            setShowStatusModal(true);
                        }}
                        onSuccess={(msg, newStatus) => {
                            toast.success(msg);
                            dispatch(updateTaskStatus({ id: row.original.id, status: newStatus }));
                            // Still do a background fetch to sync with any side-effects
                            setRefreshSignal(prev => prev + 1);
                        }}
                        onCompleted={handleCompletedStatus}
                    />
                ),
            },
            {
                id: "createdBy",
                accessorFn: (row) => row.assignedBy?.firstName || row.GivenByFirstName || "",
                header: "Created By",
                size: 110,
                enableSorting: true,
                Cell: ({ row }) => {
                    const t = row.original;
                    const firstName = t.assignedBy?.firstName || t.GivenByFirstName || "";
                    const lastName = t.assignedBy?.lastName || t.GivenByLastName || "";
                    const email = t.assignedBy?.email || t.GivenByEmail || "";
                    return (
                        <Box className="taskboard-avatar-cell" sx={{ display: "flex", alignItems: "center" }}>
                            <UserAvatarTooltip
                                firstName={firstName}
                                lastName={lastName}
                                email={email}
                            />
                        </Box>
                    );
                },
            },
            {
                id: "assignedTo",
                accessorFn: (row) => row.normalizedAssignee?.firstName || row.FirstName || "",
                header: "Assigned To",
                size: 130,
                enableSorting: true,
                Cell: ({ row }) => {
                    const t = row.original;
                    const firstName = t.normalizedAssignee?.firstName || t.FirstName || "";
                    const lastName = t.normalizedAssignee?.lastName || t.LastName || "";
                    const email = t.normalizedAssignee?.email || t.email || "";
                    return (
                        <Box className="taskboard-avatar-cell" sx={{ display: "flex", alignItems: "center" }}>
                            <UserAvatarTooltip
                                firstName={firstName}
                                lastName={lastName}
                                email={email}
                            />
                        </Box>
                    );
                },
            },
        ],
        [dispatch, handleCompletedStatus, userId]
    );

    // ── Active Pills Logic ──────────────────────────────────────────────────
    const activePills = useMemo(() => {
        const pills = [];

        // Show search chip only for the applied search (Enter / Apply), not while typing.
        if (globalFilter) {
            pills.push({
                id: "search",
                label: `Search: "${globalFilter}"`,
                onDelete: () => {
                    setStagedSearch("");
                    const newFilters = Object.entries(stagedFilters)
                        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                        .map(([id, v]) => ({ id, value: v }));
                    applyFiltersAndSearch(newFilters, "");
                },
            });
        }

        // Remove a single value from a (possibly multi-value) column filter.
        const removeFilterValue = (f, normalizedId, valueToRemove) => {
            const remaining = splitFilterValues(f.value).filter((v) => String(v) !== String(valueToRemove));
            const next = remaining.length
                ? columnFilters.map((cf) => (cf.id === f.id ? { ...cf, value: remaining.join(",") } : cf))
                : columnFilters.filter((cf) => cf.id !== f.id);
            onColumnFiltersChange(next);
            setStagedFilters((prev) => {
                const np = { ...prev };
                if (remaining.length) np[normalizedId] = remaining.join(",");
                else delete np[normalizedId];
                return np;
            });
        };

        const assignedByValues = splitFilterValues(stagedFilters.assignedBy);
        const assignedToValues = splitFilterValues(stagedFilters.assignedTo);

        columnFilters.forEach((f) => {
            const id = legacyMap[f.id] || f.id;

            // Date range is a single combined pill (two separate filters).
            if (id === "due_date_start" || id === "due_date_end") {
                if (!pills.find((p) => p.id === "date_range")) {
                    const startF = columnFilters.find((cf) => legacyMap[cf.id] === "due_date_start" || cf.id === "due_date_start" || cf.id === "startDate");
                    const endF = columnFilters.find((cf) => legacyMap[cf.id] === "due_date_end" || cf.id === "due_date_end" || cf.id === "endDate");
                    const startVal = startF?.value;
                    const endVal = endF?.value;

                    let rangeLabel = "";
                    if (startVal && endVal) rangeLabel = `Date: ${startVal} to ${endVal}`;
                    else if (startVal) rangeLabel = `From: ${startVal}`;
                    else if (endVal) rangeLabel = `To: ${endVal}`;

                    if (rangeLabel) {
                        pills.push({
                            id: "date_range",
                            label: rangeLabel,
                            onDelete: () => {
                                const next = columnFilters.filter((cf) => {
                                    const nid = legacyMap[cf.id] || cf.id;
                                    return nid !== "due_date_start" && nid !== "due_date_end" && nid !== "startDate" && nid !== "endDate";
                                });
                                onColumnFiltersChange(next);
                                setStagedFilters((prev) => ({ ...prev, due_date_start: "", due_date_end: "" }));
                            },
                        });
                    }
                }
                return;
            }

            // Other filters may hold multiple comma-joined values → render one pill each.
            splitFilterValues(f.value).forEach((value) => {
                let label = null;
                if (id === "department") {
                    const dept = facetOptions.departments.find((d) => String(d.id || d.Id) === String(value)) ||
                        departments.find((d) => String(d.id || d.Id) === String(value));
                    label = `Dept: ${dept?.name || dept?.label || f.label || "Department"}`;
                } else if (id === "project") {
                    const proj = facetOptions.projects.find((p) => String(p.id) === String(value)) ||
                        projects.find((p) => String(p.id) === String(value));
                    if (proj) label = `Project: ${proj.name}`;
                } else if (id === "group_id") {
                    const grp = facetOptions.groups.find((g) => String(g.id) === String(value)) ||
                        groups.find((g) => String(g.id) === String(value));
                    if (grp) label = `Group: ${grp.groupName}`;
                } else if (id === "priority") {
                    label = `Priority: ${value}`;
                } else if (id === "status") {
                    if (String(value).toLowerCase() !== "all") {
                        label = `Status: ${value}`;
                    }
                } else if (id === "ageing") {
                    const opt = ageingFilterOptions.find((o) => o.value === value);
                    if (opt) label = `Overdue: ${opt.label}`;
                }

                if (label) {
                    pills.push({
                        id: `${id}:${value}`,
                        label,
                        onDelete: () => removeFilterValue(f, id, value),
                    });
                }
            });
        });

        // Pills for filters that go through stagedFilters directly (not columnFilters)
        const findPersonLabel = (userId) => {
            const cachedFilter = columnFilters.find(f => (f.id === 'teamMembers' || f.id === 'assignedTo' || f.id === 'createdBy') && String(f.value) === String(userId) && f.label);
            if (cachedFilter) return cachedFilter.label;

            const allPeople = [...(facetOptions.assigners || []), ...(facetOptions.assignees || []), ...(facetOptions.employees || []), ...(reduxEmployees || [])];
            const emp = allPeople.find((e) => e && String(e.userId || e.id) === String(userId));
            if (!emp) return null;
            const fn = emp.FirstName || emp.first_name || emp.firstName || "";
            const ln = emp.LastName || emp.last_name || emp.lastName || "";
            return `${fn} ${ln}`.trim() || emp.email || null;
        };

        const resolvePersonChipValues = (values, options) => {
            if (values.some((v) => String(v).toLowerCase() === "all")) {
                return (options || [])
                    .map((e) => String(e.userId || e.id || ""))
                    .filter(Boolean);
            }
            return values.filter((v) => String(v).toLowerCase() !== "all");
        };

        const pushPersonChips = (values, options, filterKey, labelPrefix) => {
            resolvePersonChipValues(values, options).forEach((value) => {
                if (pills.find((p) => p.id === `${filterKey}:${value}`)) return;
                const isSelf = userId && String(value) === String(userId);
                const name =
                    isSelf && filterKey === "assignedTo" && activeTab === "my"
                        ? "Me"
                        : findPersonLabel(value);
                if (!name) return;
                pills.push({
                    id: `${filterKey}:${value}`,
                    label: `${labelPrefix}: ${name}`,
                    onDelete: () => {
                        const currentValues = resolvePersonChipValues(
                            splitFilterValues(stagedFilters[filterKey]),
                            options
                        );
                        const remaining = currentValues.filter((v) => String(v) !== String(value));
                        const next = { ...stagedFilters };
                        if (remaining.length) next[filterKey] = remaining.join(",");
                        else delete next[filterKey];
                        setStagedFilters(next);
                        const newFilters = Object.entries(next)
                            .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                            .map(([id, v]) => ({ id, value: v }));
                        applyFiltersAndSearch(newFilters, stagedSearch);
                    },
                });
            });
        };

        // Created By / Assignee — always one chip per person (never a single "All" chip)
        pushPersonChips(assignedByValues, facetOptions.assigners, "assignedBy", "Created By");
        pushPersonChips(assignedToValues, facetOptions.assignees, "assignedTo", "Assignee");

        // Team Members pills
        const teamMemberValues = splitFilterValues(stagedFilters.teamMembers);
        const showAllTeamMembers = teamMemberValues.includes("all");

        if (showAllTeamMembers) {
            pills.push({
                id: "teamMembers:all",
                label: `Team Member: All`,
                onDelete: () => {
                    const next = { ...stagedFilters };
                    delete next.teamMembers;
                    setStagedFilters(next);
                    const newFilters = Object.entries(next)
                        .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                        .map(([id, v]) => ({ id, value: v }));
                    applyFiltersAndSearch(newFilters, stagedSearch);
                },
            });
        } else {
            teamMemberValues.forEach((value) => {
                if (pills.find((p) => p.id === `teamMembers:${value}`)) return;
                const name = findPersonLabel(value);
                pills.push({
                    id: `teamMembers:${value}`,
                    label: `Team Member: ${name || "Member"}`,
                    onDelete: () => {
                        const remaining = splitFilterValues(stagedFilters.teamMembers).filter((v) => String(v) !== String(value));
                        const next = { ...stagedFilters };
                        if (remaining.length) next.teamMembers = remaining.join(",");
                        else delete next.teamMembers;
                        setStagedFilters(next);
                        const newFilters = Object.entries(next)
                            .filter(([, v]) => v !== "" && v !== null && v !== undefined)
                            .map(([id, v]) => ({ id, value: v }));
                        applyFiltersAndSearch(newFilters, stagedSearch);
                    },
                });
            });
        }

        return pills;
    }, [globalFilter, columnFilters, stagedFilters, stagedSearch, departments, projects, groups, reduxEmployees, facetOptions, userId, activeTab, onGlobalFilterChange, onColumnFiltersChange, applyFiltersAndSearch]);

    const renderRowActions = ({ row }) => {
        const t = row.original;
        const selfId = String(userId);
        const assignedById = String(t.assignedBy?.userId || t.assignedBy?.id || t.GivenByUserId || "");
        const createdById = String(t.created_by?.id || t.created_by || "");
        const assignedBy = String(t.assigned_by || "");

        // User can only delete if they are the one who assigned the task (to themselves or others)
        const canDelete = assignedById === selfId;

        // Determine if user is the creator (same logic as handleActionClick)
        const isCreator = assignedById === selfId || (createdById !== "" && createdById === selfId) || (assignedBy !== "" && assignedBy === selfId);
        const canEdit = activeTab === "team" ? isCreator : true;

        return (
            <Box sx={{ display: "flex", gap: "2px", flexWrap: "nowrap", alignItems: "center" }}>
                <Tooltip title="View Details">
                    <IconButton
                        onClick={() => handleViewTask(t)}
                        size="small"
                        sx={{
                            color: "#64748b",
                            "&:hover": {
                                color: "#2563eb",
                                backgroundColor: "#eff6ff",
                            },
                        }}
                    >
                        <img src={viewIcon} alt="View" style={{ width: 16, height: 16, filter: "brightness(0.2)" }} />
                    </IconButton>
                </Tooltip>
                <Tooltip title={canEdit ? "Update Task" : "Disabled (Not created by you)"}>
                    <span>
                        <IconButton
                            onClick={() => handleActionClick(t)}
                            disabled={!canEdit}
                            size="small"
                            sx={{
                                color: "#64748b",
                                "&:hover": {
                                    color: "#d97706",
                                    backgroundColor: "#fffbeb",
                                },
                                "&.Mui-disabled": {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <img src={editIcon} alt="Edit" style={{ width: 16, height: 16, filter: "brightness(0.2)" }} />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title={canDelete ? "Delete Task" : "Disabled (Assigned by someone else)"}>
                    <span> {/* Span wrap for tooltip to work on disabled buttons */}
                        <IconButton
                            onClick={() => handleDeleteTask(t)}
                            disabled={!canDelete}
                            size="small"
                            sx={{
                                color: "#64748b",
                                "&:hover": {
                                    color: "#dc2626",
                                    backgroundColor: "#fef2f2",
                                },
                                "&.Mui-disabled": {
                                    opacity: 0.3,
                                }
                            }}
                        >
                            <img src={binIcon} alt="Delete" style={{ width: 16, height: 16, filter: "brightness(0.2)" }} />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        );
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <>
            <div
                className="task-page-container taskboard-container"
                style={{ height: "calc(100vh - 15px)", display: "flex", flexDirection: "column" }}
            >
                <TaskPageHeader
                    tabs={
                        hasSubordinates
                            ? [
                                { label: "My Tasks", value: "my" },
                                { label: "My Team's Tasks", value: "team" },
                            ]
                            : [{ label: "My Tasks", value: "my" }]
                    }
                    activeTab={activeTab}
                    onTabChange={handleTabChange}
                    summaryCards={summaryCards}
                    onOpenFilters={(e) => setFilterAnchorEl(e.currentTarget)}
                    activeFilterCount={activeFilterCount}
                    buttons={[
                        {
                            label: "Refresh",
                            icon: <SyncOutlinedIcon fontSize="small" />,
                            iconOnly: true,
                            onClick: () => setRefreshSignal((prev) => prev + 1),
                            disabled: isLoading || isRefetching || isSummaryLoading,
                        },
                        {
                            label: "Export All",
                            icon: <FileDownloadOutlinedIcon fontSize="small" />,
                            iconOnly: true,
                            onClick: handleExportTasks,
                            loading: isExporting,
                        },
                        {
                            label: "Import Bulk Tasks",
                            icon: <FileUploadOutlinedIcon fontSize="small" />,
                            iconOnly: true,
                            onClick: () => setShowBulkUpload(true),
                            disabled: isLoading || isRefetching,
                        },
                        {
                            label: "Create Task",
                            icon: <FaPlus size={14} />,
                            onClick: () => setShowAddTask(true),
                            disabled: isLoading || isRefetching,
                            color: "error"
                        },
                    ]}
                    statusSummary={statusCards}
                    currentStatus={currentStatus}
                    onStatusFilter={setStatusFilter}
                    tasks={tasks}
                    statusCounts={statusCounts}
                    isApplying={isLoading || isRefetching || isSummaryLoading}
                    filters={{
                        departments: {
                            // Prefer facet options (contextual); fall back to all departments if facets not yet loaded
                            options: facetOptions.departments.length > 0 ? facetOptions.departments : departments,
                            value: (
                                facetOptions.departments.find((d) => String(d.id) === currentDept) ||
                                departments.find((d) => String(d.id) === currentDept) ||
                                null
                            ),
                        },
                        projects: {
                            options: facetOptions.projects.length > 0 ? facetOptions.projects : projects,
                            value: (
                                facetOptions.projects.find((p) => String(p.id) === currentProject) ||
                                projects.find((p) => String(p.id) === currentProject) ||
                                null
                            ),
                        },
                        groups: {
                            options: facetOptions.groups.length > 0 ? facetOptions.groups : groups,
                            value: (
                                facetOptions.groups.find((g) => String(g.id) === currentGroup) ||
                                groups.find((g) => String(g.id) === currentGroup) ||
                                null
                            ),
                        },
                        employees: {
                            // Facet employees have first_name/last_name; reduxEmployees have FirstName/LastName
                            options: facetOptions.employees.length > 0
                                ? facetOptions.employees.map((e) => ({
                                    ...e,
                                    // Normalize to camelCase so TaskPageHeader's getOptionLabel works
                                    firstName: e.first_name || e.firstName || "",
                                    lastName: e.last_name || e.lastName || "",
                                }))
                                : reduxEmployees,
                            value: (
                                facetOptions.employees.find((e) => String(e.id) === currentEmp) ||
                                reduxEmployees.find((e) => String(e.userId || e.id) === currentEmp) ||
                                null
                            ),
                        },
                        ageing: {
                            options: ageingFilterOptions,
                            value: ageingFilterOptions.find((o) => o.value === currentAgeing) || null,
                        },
                        priority: {
                            options: priorityFilterOptions,
                            value: priorityFilterOptions.find((o) => o.value === currentPrio) || null,
                        },
                        targetEndDate: {
                            value: { start: currentStartDate, end: currentEndDate },
                        },
                    }}
                    onFilterChange={(type, val) => {
                        let id = type;
                        if (type === "department") id = "department";
                        if (type === "project") id = "project";
                        if (type === "group") id = "group_id";
                        if (type === "employee") id = "assignedBy";
                        if (type === "ageing") id = "ageing";

                        if (type === "targetEndDate") {
                            const next = {
                                ...stagedFilters,
                                due_date_start: val?.start || "",
                                due_date_end: val?.end || "",
                            };
                            setStagedFilters(next);

                            const newFilters = Object.entries(next)
                                .filter(([id, value]) => value !== "" && value !== null && value !== undefined)
                                .map(([id, value]) => ({ id, value }));
                            applyFiltersAndSearch(newFilters, stagedSearch);
                            return;
                        }
                        const valToSet = val ? val.id || val.userId || val.value || (typeof val === 'string' ? val : "") : "";
                        setStagedFilters((prev) => ({ ...prev, [id]: valToSet }));
                    }}
                    globalFilter={stagedSearch}
                    onGlobalFilterChange={setStagedSearch}
                    onApply={() => {
                        const newFilters = Object.entries(stagedFilters)
                            .filter(([id, value]) => value !== "" && value !== null && value !== undefined)
                            .map(([id, value]) => ({ id, value }));

                        applyFiltersAndSearch(newFilters, stagedSearch);
                    }}
                    onReset={() => {
                        setStagedFilters({});
                        setStagedSearch("");
                        applyFiltersAndSearch([], "");
                        onColumnFiltersChange([]);
                    }}
                    activePills={activePills}
                    createdByCreators={facetOptions.assigners}
                    selectedCreatedByUserId={selectedCreatedByUserId}
                    currentUserId={userId}
                    onCreatedBySelect={handleCreatedBySelect}
                    hideStatusRow
                />

                <div className="taskboard-content-card">
                    <div className="taskboard-content-card__filters">
                        <TaskStatusFilterRow
                            statusSummary={statusCards}
                            currentStatus={currentStatus}
                            onStatusFilter={setStatusFilter}
                            tasks={tasks}
                            statusCounts={statusCounts}
                            isApplying={isLoading || isRefetching}
                            createdByCreators={facetOptions.assigners}
                            selectedCreatedByUserId={selectedCreatedByUserId}
                            currentUserId={userId}
                            onCreatedBySelect={handleCreatedBySelect}
                        />
                    </div>

                    <div className="taskboard-table-wrapper">
                        <AppDataTable
                            columns={columns}
                            data={tasks}
                            rowCount={totalRows}
                            state={{ pagination, sorting, globalFilter, columnFilters }}
                            onPaginationChange={onPaginationChange}
                            onSortingChange={onSortingChange}
                            onColumnFiltersChange={onColumnFiltersChange}
                            enableGlobalFilter={false}
                            isLoading={isLoading}
                            isRefetching={isRefetching}
                            enableRowSelection={false}
                            rowSelection={rowSelection}
                            onRowSelectionChange={setRowSelection}
                            renderRowActions={renderRowActions}
                            renderTopToolbarCustomActions={({ table }) => {
                                const isSelected = table.getIsSomeRowsSelected() || table.getIsAllRowsSelected();
                                if (!isSelected) return null;
                                const count = table.getSelectedRowModel().flatRows.length;
                                return (
                                    <Box sx={{ display: 'flex', gap: '8px', pl: 1 }}>
                                        <CustomButton
                                            size="small"
                                            className="task-action-btn"
                                            onClick={handleBulkComplete}
                                            loading={bulkCompleteLoading}
                                            disabled={isLoading || isRefetching}
                                            sx={{ backgroundColor: '#4caf50', borderRadius: '8px', height: '32px' }}
                                        >
                                            Mark Completed ({count})
                                        </CustomButton>
                                        <CustomButton
                                            size="small"
                                            className="task-action-btn"
                                            onClick={handleBulkDelete}
                                            loading={bulkCompleteLoading}
                                            disabled={isLoading || isRefetching}
                                            sx={{ backgroundColor: '#dc2626', borderRadius: '8px', height: '32px' }}
                                        >
                                            Delete ({count})
                                        </CustomButton>
                                    </Box>
                                );
                            }}

                        />
                    </div>
                </div>
            </div>

            {/* Task Details Drawer */}
            <AppDrawer
                open={showTaskDrawer}
                onClose={() => {
                    setShowTaskDrawer(false);
                    resetWorkLogSignal();
                }}
                title={selectedTask?.title || "Task Details"}
            >
                <TaskDetailsModal
                    task={selectedTask}
                    variant="drawer"
                    open={showTaskDrawer}
                    showGivenBy={true}
                    externalWorkLogOpenSignal={workLogOpenSignal}
                    onClose={() => {
                        setShowTaskDrawer(false);
                        resetWorkLogSignal();
                    }}
                    onDelegateClick={(t) => {
                        setShowTaskDrawer(false);
                        setActionTask({ ...t, status: "delegated" });
                        setShowStatusModal(true);
                    }}
                    onStatusSuccess={(msg, newStatus, taskId) => {
                        toast.success(msg);
                        if (taskId) {
                            dispatch(updateTaskStatus({ id: taskId, status: newStatus }));
                        }
                        setRefreshSignal(prev => prev + 1);
                    }}
                />
            </AppDrawer>

            {/* Bulk Complete Confirmation */}
            {showBulkCompleteModal && (
                <div className="action-modal-overlay">
                    <div className="action-modal">
                        <h3>Confirm Bulk Complete</h3>
                        <p>
                            Are you sure you want to mark {bulkCompleteTasks.length} task
                            {bulkCompleteTasks.length !== 1 ? "s" : ""} as Completed?
                        </p>
                        <div className="bulk-confirm-actions">
                            <button
                                onClick={() => setShowBulkCompleteModal(false)}
                                className="taskboard-btn bulk-btn-cancel"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={executeBulkComplete}
                                className="taskboard-btn bulk-btn-confirm"
                                disabled={bulkCompleteLoading}
                            >
                                {bulkCompleteLoading ? "Processing…" : "Confirm"}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* ── Status Update Modal (extracted component) ── */}
            {showStatusModal && (
                <TaskStatusModal
                    task={actionTask}
                    userId={userId}
                    delegateOptions={delegateOptions}
                    onClose={() => {
                        setShowStatusModal(false);
                        setActionTask(null);
                    }}
                    onSuccess={(msg, newStatus) => {
                        toast.success(msg);
                        setRefreshSignal(prev => prev + 1);
                        setShowStatusModal(false);
                        setActionTask(null);
                    }}
                    onCompleted={handleCompletedStatus}
                />
            )}

            {/* ── Edit Task Modal ── */}
            {showEditTaskModal && (
                <EditTaskModal
                    open={showEditTaskModal}
                    task={actionTask}
                    onClose={() => {
                        setShowEditTaskModal(false);
                        setActionTask(null);
                    }}
                    onTaskUpdated={() => {
                        toast.success("Task updated successfully!");
                        setRefreshSignal(prev => prev + 1);
                        setShowEditTaskModal(false);
                        setActionTask(null);
                    }}
                />
            )}

            {/* Bulk Upload Modal */}
            <BulkUploadModal
                open={showBulkUpload}
                onClose={() => setShowBulkUpload(false)}
                onBulkUploadSuccess={() => {
                    setShowBulkUpload(false);
                    toast.success("Bulk upload successful!");
                    setRefreshSignal(prev => prev + 1);
                }}
            />

            {/* Add Task Modal */}
            {showAddTask && (
                <AddTaskModal
                    open={showAddTask}
                    onClose={() => setShowAddTask(false)}
                    onTaskCreated={() => {
                        setRefreshSignal(prev => prev + 1);
                    }}
                />
            )}

            <TaskFilterDialog
                open={Boolean(filterAnchorEl)}
                anchorEl={filterAnchorEl}
                onClose={() => { setFilterAnchorEl(null); setDialogDraft({}); }}
                categories={activeFilterCategories}
                optionsByCategory={filterOptionsByCategory}
                initialSelected={filterSelected}
                onApply={handleApplyDialogFilters}
                onSelectionChange={(draft) => setDialogDraft(draft)}
                teamMembersType={activeTab === "team" ? "my-team" : "me"}
            />
        </>
    );
};

export default TaskBoard;
